import { Input } from "@/components/ui/input";
import OrganismSearch from "@/components/common/OrganismSearch";
import AlignmentToolSelect from "@/components/common/AlignmentToolSelect";
import MetadataBuilder from "@/components/common/MetadataBuilder";
import ComparisonBuilder from "@/components/common/ComparisonBuilder";
import { useState } from "react";

const MIN_PHRED_QUALITY_SCORE = 10;
const MAX_PHRED_QUALITY_SCORE = 40;
const MIN_READ_LENGTH = 50;
const MAX_READ_LENGTH = 120;
const MIN_LOG2_FOLD_CHANGE = 0.25;
const MAX_LOG2_FOLD_CHANGE = 5;


export const defaultValues = {
  organism: "",
  alignment_tool: "",
  metadata_builder: [
    {
      sample_id: "",
      condition: "",
    },
  ],
  comparisons: {
    auto_generate: false,
    saved: false,
    items: [],
  },
  raw_data_qc: {
    phred_quality_score: 30,
    minimum_read_length_required: 75,
  },
  differential_gene_expression: {
    log2_fold_change: 0.25,
    threshold_type: "p-value",
    threshold_value: 0.05,
  },
  annotation: {
    threshold_type: "p-value",
    threshold_value: 0.05,
  },
};

export const validate = (values = {}) => {
  const errors = {};
  const rawDataQc = values.raw_data_qc || {};
  const differentialGeneExpression = values.differential_gene_expression || {};
  const annotation = values.annotation || {};
  const metadata = values.metadata_builder || [];

  if (!String(values.organism || "").trim()) {
    errors.organism = "Organism is required.";
  }
  if (!String(values.alignment_tool || "").trim()) {
    errors.alignment_tool = "Alignment tool is required.";
  }


  if (metadata.length === 0) {
    errors.metadata_builder = "At least one sample is required.";
  } else {
    const sampleIds = new Set();

    metadata.forEach((row, index) => {
      const sampleId = String(row.sample_id || "").trim();
      const condition = String(row.condition || "").trim();

      if (!sampleId) {
        errors[`sample_id_${index}`] = "Sample ID is required.";
      } else if (sampleIds.has(sampleId.toLowerCase())) {
        errors[`sample_id_${index}`] = "Duplicate Sample ID.";
      } else {
        sampleIds.add(sampleId.toLowerCase());
      }

      if (!condition) {
        errors[`condition_${index}`] = "Condition is required.";
      }
    });
  }

  const uniqueConditions = new Set(
    metadata
      .map((row) => String(row.condition || "").trim().toLowerCase())
      .filter(Boolean)
  );

  if (uniqueConditions.size < 2) {
    errors.metadata_builder =
      "At least two unique conditions are required to perform comparisons.";
  }

  if (rawDataQc.phred_quality_score === "" || rawDataQc.phred_quality_score === null || rawDataQc.phred_quality_score === undefined) {
    errors.phred_quality_score = "Phred quality score is required.";
  } else {
    const numericValue = Number(rawDataQc.phred_quality_score);
    if (Number.isNaN(numericValue) || numericValue < MIN_PHRED_QUALITY_SCORE || numericValue > MAX_PHRED_QUALITY_SCORE) {
      errors.phred_quality_score = `Value must be between ${MIN_PHRED_QUALITY_SCORE} and ${MAX_PHRED_QUALITY_SCORE}.`;
    }
  }

  if (rawDataQc.minimum_read_length_required === "" || rawDataQc.minimum_read_length_required === null || rawDataQc.minimum_read_length_required === undefined) {
    errors.minimum_read_length_required = "Minimum read length is required.";
  } else {
    const numericValue = Number(rawDataQc.minimum_read_length_required);
    if (Number.isNaN(numericValue) || numericValue < MIN_READ_LENGTH || numericValue > MAX_READ_LENGTH) {
      errors.minimum_read_length_required = `Value must be between ${MIN_READ_LENGTH} and ${MAX_READ_LENGTH}.`;
    }
  }

  if (differentialGeneExpression.log2_fold_change === "" || differentialGeneExpression.log2_fold_change === null || differentialGeneExpression.log2_fold_change === undefined) {
    errors.log2_fold_change = "Log2 fold change is required.";
  } else if (Number.isNaN(Number(differentialGeneExpression.log2_fold_change))) {
    errors.log2_fold_change = "Log2 fold change must be a number.";
  }

  if (!String(differentialGeneExpression.threshold_type || "").trim()) {
    errors.differential_threshold_type =
      "Please select a differential expression threshold type.";
  } else if (
    differentialGeneExpression.threshold_value === "" ||
    differentialGeneExpression.threshold_value === null ||
    differentialGeneExpression.threshold_value === undefined
  ) {
    errors.differential_threshold_value = "Threshold value is required.";
  } else {
    const numericValue = Number(
      differentialGeneExpression.threshold_value
    );

    if (
      Number.isNaN(numericValue) ||
      numericValue < 0 ||
      numericValue > 1
    ) {
      errors.differential_threshold_value =
        "Value must be between 0 and 1.";
    }
  }

  if (!String(annotation.threshold_type || "").trim()) {
    errors.annotation_threshold_type =
      "Please select an annotation threshold type.";
  } else if (
    annotation.threshold_value === "" ||
    annotation.threshold_value === null ||
    annotation.threshold_value === undefined
  ) {
    errors.annotation_threshold_value = "Threshold value is required.";
  } else {
    const numericValue = Number(annotation.threshold_value);

    if (
      Number.isNaN(numericValue) ||
      numericValue < 0 ||
      numericValue > 1
    ) {
      errors.annotation_threshold_value =
        "Value must be between 0 and 1.";
    }
  }


  const differential = values.differential_gene_expression || {};

  if (
    differential.log2_fold_change === "" ||
    differential.log2_fold_change === null ||
    differential.log2_fold_change === undefined
  ) {
    errors.log2_fold_change = "Log2 Fold Change is required.";
  } else {
    const numericValue = Number(differential.log2_fold_change);

    if (
      Number.isNaN(numericValue) ||
      numericValue < MIN_LOG2_FOLD_CHANGE ||
      numericValue > MAX_LOG2_FOLD_CHANGE
    ) {
      errors.log2_fold_change =
        `Value must be between ${MIN_LOG2_FOLD_CHANGE} and ${MAX_LOG2_FOLD_CHANGE}.`;
    }
  }

  return errors;
};

export default function TranscriptomeResequencingParameters({
  values = {},
  onChange,
  errors = {},
  expectedSamples,
}) {
  const setValue = (key, value) => onChange(key, value);
  const [metadataLocked, setMetadataLocked] = useState(false);

  const updateNestedValue = (sectionKey, key, value) => {
    setValue(sectionKey, { ...(values[sectionKey] || {}), [key]: value });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-[#E5E7EB] bg-[#F8FAF7] p-4">
        <h3 className="text-sm font-semibold text-[#09582D]">Basic Analysis Parameters</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Organism</label>
            <OrganismSearch
              value={values.organism || ""}
              onChange={(organism) =>
                setValue("organism", organism?.renderName)
              }
            />
            {errors.organism && <p className="mt-1 text-xs text-red-600">{errors.organism}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Alignment Tool</label>
            <AlignmentToolSelect
              value={values.alignment_tool || ""}
              onChange={(value) => setValue("alignment_tool", value)}
              options={["STAR"]}
            />
            {errors.alignment_tool && <p className="mt-1 text-xs text-red-600">{errors.alignment_tool}</p>}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[#E5E7EB] p-4">
        <MetadataBuilder
          value={values.metadata_builder || []}
          onChange={(metadata) => setValue("metadata_builder", metadata)}
          errors={errors}
          expectedSamples={expectedSamples}
          onLockChange={setMetadataLocked}
          columns={[
            {
              key: "sample_id",
              label: "Sample ID",
            },
            {
              key: "condition",
              label: "Condition",
            },
          ]}
        />
        {metadataLocked && (
          <div className="mt-6 rounded-lg border border-[#E5E7EB] p-4">
            <ComparisonBuilder
              metadata={values.metadata_builder || []}
              value={values.comparisons || {}}
              onChange={(comparisons) => setValue("comparisons", comparisons)}
              errors={errors}
            />
          </div>
        )}
      </div>

      <div className="rounded-lg border border-[#E5E7EB] p-4">
        <h3 className="text-sm font-semibold text-gray-800">Raw Data QC Parameters</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Phred Quality Score</label>
            <Input
              type="number"
              min={MIN_PHRED_QUALITY_SCORE}
              max={MAX_PHRED_QUALITY_SCORE}
              value={values.raw_data_qc?.phred_quality_score ?? ""}
              onChange={(e) => updateNestedValue("raw_data_qc", "phred_quality_score", e.target.value)}
              className="w-full"
            />
            <p className="mt-1 text-xs text-gray-500">Allowed range: {MIN_PHRED_QUALITY_SCORE} to {MAX_PHRED_QUALITY_SCORE}</p>
            {errors.phred_quality_score && <p className="mt-1 text-xs text-red-600">{errors.phred_quality_score}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Minimum Read Length Required</label>
            <Input
              type="number"
              min={MIN_READ_LENGTH}
              max={MAX_READ_LENGTH}
              value={values.raw_data_qc?.minimum_read_length_required ?? ""}
              onChange={(e) => updateNestedValue("raw_data_qc", "minimum_read_length_required", e.target.value)}
              className="w-full"
            />
            <p className="mt-1 text-xs text-gray-500">Allowed range: {MIN_READ_LENGTH} to {MAX_READ_LENGTH}</p>
            {errors.minimum_read_length_required && <p className="mt-1 text-xs text-red-600">{errors.minimum_read_length_required}</p>}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[#E5E7EB] p-4">
        <h3 className="text-sm font-semibold text-gray-800">Thresholds for Differential Gene Expression</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Log2 Fold Change</label>
            <Input
              type="number"
              min={MIN_LOG2_FOLD_CHANGE}
              max={MAX_LOG2_FOLD_CHANGE}
              step={0.01}
              value={values.differential_gene_expression?.log2_fold_change ?? ""}
              onChange={(e) =>
                updateNestedValue(
                  "differential_gene_expression",
                  "log2_fold_change",
                  e.target.value
                )
              }
              className="w-full"
            />

            <p className="mt-1 text-xs text-gray-500">
              Allowed range: {MIN_LOG2_FOLD_CHANGE} to {MAX_LOG2_FOLD_CHANGE}
            </p>

            {errors.log2_fold_change && (
              <p className="mt-1 text-xs text-red-600">
                {errors.log2_fold_change}
              </p>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Significance Threshold</label>
            <div className="space-y-3">
              {[
                { value: "p-value", label: "p-value" },
                { value: "q-value", label: "q-value" },
              ].map((option) => {
                const selected = (values.differential_gene_expression?.threshold_type || "") === option.value;
                return (
                  <label key={option.value} className="flex items-start gap-3 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="differential-threshold"
                      checked={selected}
                      onChange={() => updateNestedValue("differential_gene_expression", "threshold_type", option.value)}
                      className="h-4 w-4 border-gray-300 text-[#538135] focus:ring-[#EAF4E3]"
                    />
                    <span>{option.label}</span>
                    {selected && (
                      <div className="ml-2 flex flex-col">
                        <Input
                          type="number"
                          min={0}
                          max={1}
                          step={0.01}
                          value={values.differential_gene_expression?.threshold_value ?? ""}
                          onChange={(e) =>
                            updateNestedValue(
                              "differential_gene_expression",
                              "threshold_value",
                              e.target.value
                            )
                          }
                          className="w-32"
                          placeholder="0.00 - 1.00"
                        />

                        <p className="mt-1 text-xs text-gray-500">
                          Range: 0 - 1
                        </p>
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
            {errors.differential_threshold_type && <p className="mt-2 text-xs text-red-600">{errors.differential_threshold_type}</p>}
            {errors.differential_threshold_value && <p className="mt-2 text-xs text-red-600">{errors.differential_threshold_value}</p>}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[#E5E7EB] p-4">
        <h3 className="text-sm font-semibold text-gray-800">Annotation Parameters</h3>
        <div className="mt-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Significance Threshold for Enrichment</label>
          <div className="space-y-3">
            {[
              { value: "p-value", label: "p-value" },
              { value: "q-value (FDR)", label: "q-value (FDR)" },
            ].map((option) => {
              const selected = (values.annotation?.threshold_type || "") === option.value;
              return (
                <label key={option.value} className="flex items-start gap-3 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="annotation-threshold"
                    checked={selected}
                    onChange={() => updateNestedValue("annotation", "threshold_type", option.value)}
                    className="h-4 w-4 border-gray-300 text-[#538135] focus:ring-[#EAF4E3]"
                  />
                  <span>{option.label}</span>
                  {selected && (
                    <div className="ml-2 flex flex-col">
                      <Input
                        type="number"
                        min={0}
                        max={1}
                        step={0.01}
                        value={values.annotation?.threshold_value ?? ""}
                        onChange={(e) =>
                          updateNestedValue(
                            "annotation",
                            "threshold_value",
                            e.target.value
                          )
                        }
                        className="w-32"
                        placeholder="0.00 - 1.00"
                      />

                      <p className="mt-1 text-xs text-gray-500">
                        Range: 0 - 1
                      </p>
                    </div>
                  )}
                </label>
              );
            })}
          </div>
          {errors.annotation_threshold_type && <p className="mt-2 text-xs text-red-600">{errors.annotation_threshold_type}</p>}
          {errors.annotation_threshold_value && <p className="mt-2 text-xs text-red-600">{errors.annotation_threshold_value}</p>}
        </div>
      </div>
    </div>
  );
}
